import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KpiCard from "@/components/common/KpiCard";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { formatEuro, formatPercent, formatNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar, AreaChart, Area,
} from "recharts";
import {
  TrendingUp, Coins, Banknote, PiggyBank, Building2, Percent, Target,
  ArrowUpRight, ArrowDownRight, PlayCircle, PlusCircle, History, Wallet,
  AlertTriangle, Info as InfoIcon, RefreshCw, ChevronRight,
} from "lucide-react";
import SmartLoader from "@/components/common/SmartLoader";

const CHART_GRID = "#E5E1D6";
const COLOR_PRIMARY = "#112F24";
const COLOR_ACCENT = "#B58C4B";
const COLOR_EMERALD = "#047857";
const COLOR_ROSE = "#B91C1C";

export default function PerformancePage() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="animate-fade-up" data-testid="performance-page">
      <PageHeader
        overline="Analyse financière"
        title="Performance & rentabilité"
        description="Rendements, cash-flow consolidé, projections, objectifs, simulations de revente et refinancement."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-8 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="perf-tab-overview">Vue d&apos;ensemble</TabsTrigger>
          <TabsTrigger value="yields" data-testid="perf-tab-yields">Rendements</TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="perf-tab-cashflow">Cash-flow</TabsTrigger>
          <TabsTrigger value="projection" data-testid="perf-tab-projection">Projection 12 mois</TabsTrigger>
          <TabsTrigger value="targets" data-testid="perf-tab-targets">Objectifs</TabsTrigger>
          <TabsTrigger value="simulations" data-testid="perf-tab-simulations">Simulations</TabsTrigger>
          <TabsTrigger value="history" data-testid="perf-tab-history">Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="yields"><YieldsTab /></TabsContent>
        <TabsContent value="cashflow"><CashflowTab /></TabsContent>
        <TabsContent value="projection"><ProjectionTab /></TabsContent>
        <TabsContent value="targets"><TargetsTab /></TabsContent>
        <TabsContent value="simulations"><SimulationsTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------- Overview --------------------- */
function OverviewTab() {
  const [dash, setDash] = useState(null);
  const [evolution, setEvolution] = useState([]);
  useEffect(() => {
    api.get("/performance/dashboard").then(({ data }) => setDash(data));
    api.get("/performance/evolution").then(({ data }) => setEvolution(data.series || []));
  }, []);
  if (!dash) return <Loading />;
  const t = dash.totals;
  const y = dash.yields_pct;

  return (
    <div className="space-y-8">
      {dash.coherence_alerts?.length > 0 && (
        <CoherenceAlerts alerts={dash.coherence_alerts} />
      )}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard name="patrimoine-net" label="Patrimoine net" icon={PiggyBank} tone="accent" value={formatEuro(t.patrimoine_net)} />
        <KpiCard name="cashflow-monthly" label="Cash-flow mensuel" icon={Coins} value={formatEuro(t.cashflow_monthly)} />
        <KpiCard name="debt-ratio" label="Taux d'endettement (LTV)" icon={Percent} value={formatPercent(t.debt_ratio_ltv_pct)} />
        <KpiCard name="gross-yield" label="Rendement brut" icon={TrendingUp} value={formatPercent(y.gross)} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elevated p-6">
          <h3 className="font-heading text-lg mb-4">Rendements consolidés</h3>
          <div className="space-y-4">
            <YieldBar label="Rendement brut" value={y.gross} color={COLOR_PRIMARY} />
            <YieldBar label="Rendement net" value={y.net} color={COLOR_ACCENT} />
            <YieldBar label="Rendement net-net (après IS)" value={y.net_net} color={COLOR_EMERALD} />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <Info label="Loyers annuels cibles" value={formatEuro(t.annual_rent_target)} />
            <Info label="Loyers annuels réels" value={formatEuro(t.annual_rent_actual)} />
            <ChargesInfo total={t.annual_charges} />
            <Info label="Intérêts 12 mois" value={formatEuro(t.annual_interests_next_12m)} />
            <Info label="Impôts estimés" value={formatEuro(t.annual_taxes_estimate)} />
            <Info label="Perte vacance" value={formatEuro(t.annual_vacancy_loss)} />
          </div>
        </div>

        <div className="card-elevated p-6">
          <h3 className="font-heading text-lg mb-4">Évolution du patrimoine</h3>
          {evolution.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={evolution}>
                <defs>
                  <linearGradient id="gradPatri" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR_PRIMARY} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={COLOR_PRIMARY} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gradDebt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR_ROSE} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={COLOR_ROSE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" />
                <XAxis dataKey="year" stroke="#8B8578" />
                <YAxis stroke="#8B8578" tickFormatter={(v) => `${Math.round(v / 1000)}k€`} />
                <Tooltip formatter={(v) => formatEuro(v)} />
                <Legend />
                <Area type="monotone" dataKey="current_value" name="Valeur estimée" stroke={COLOR_PRIMARY} fill="url(#gradPatri)" />
                <Area type="monotone" dataKey="remaining_debt" name="Dette restante" stroke={COLOR_ROSE} fill="url(#gradDebt)" />
                <Line type="monotone" dataKey="patrimoine_net" name="Patrimoine net" stroke={COLOR_ACCENT} strokeWidth={2.5} dot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState label="Aucun snapshot enregistré. Capturez un instantané pour tracer l'évolution." />
          )}
        </div>
      </section>

      <section className="card-elevated overflow-x-auto">
        <div className="px-6 py-4 border-b bg-secondary/40">
          <h3 className="font-heading text-lg">Répartition par société</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/20">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-3">Société</th>
              <th className="px-6 py-3">Forme</th>
              <th className="px-6 py-3 text-right">Immeubles</th>
              <th className="px-6 py-3 text-right">Patrimoine net</th>
              <th className="px-6 py-3 text-right">Dette</th>
              <th className="px-6 py-3 text-right">Cash-flow / mois</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(dash.per_company || []).map((c) => (
              <tr key={c.company_id} data-testid={`perf-company-row-${c.company_id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{c.company_name}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{c.legal_form}</td>
                <td className="px-6 py-3 text-right font-mono">{c.building_count}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(c.net_worth)}</td>
                <td className="px-6 py-3 text-right font-mono text-muted-foreground">{formatEuro(c.remaining_debt)}</td>
                <td className={`px-6 py-3 text-right font-mono ${c.cashflow_monthly >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {formatEuro(c.cashflow_monthly)}
                </td>
              </tr>
            ))}
            {dash.per_company.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">Aucune société active.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/* --------------------- Yields --------------------- */
function YieldsTab() {
  const [dash, setDash] = useState(null);
  useEffect(() => {
    api.get("/performance/dashboard").then(({ data }) => setDash(data));
  }, []);
  if (!dash) return <Loading />;
  const rows = dash.per_building || [];
  return (
    <div className="space-y-6">
      <div className="card-elevated overflow-x-auto">
        <div className="px-6 py-4 border-b bg-secondary/40">
          <h3 className="font-heading text-lg">Rendements par immeuble</h3>
          <p className="text-xs text-muted-foreground mt-1">Brut = loyers cibles / prix d&apos;achat total. Net = après charges et intérêts (12 prochains mois).</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/20">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-3">Immeuble</th>
              <th className="px-6 py-3 text-right">Valeur</th>
              <th className="px-6 py-3 text-right">Dette</th>
              <th className="px-6 py-3 text-right">Occup.</th>
              <th className="px-6 py-3 text-right">Rdmt brut</th>
              <th className="px-6 py-3 text-right">Rdmt net</th>
              <th className="px-6 py-3 text-right">Cash-flow /mois</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.building_id} data-testid={`perf-building-row-${r.building_id}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{r.building_name}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(r.current_value)}</td>
                <td className="px-6 py-3 text-right font-mono text-muted-foreground">{formatEuro(r.remaining_debt)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatPercent(r.occupancy_pct)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatPercent(r.gross_yield_pct)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatPercent(r.net_yield_pct)}</td>
                <td className={`px-6 py-3 text-right font-mono ${r.cashflow_monthly >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {formatEuro(r.cashflow_monthly)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">Aucun immeuble.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="card-elevated p-6">
        <h3 className="font-heading text-lg mb-4">Comparatif rendement brut / net</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={rows} margin={{ top: 10, right: 20, left: -10, bottom: 30 }}>
            <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" />
            <XAxis dataKey="building_name" angle={-15} textAnchor="end" height={60} stroke="#8B8578" />
            <YAxis stroke="#8B8578" tickFormatter={(v) => `${v} %`} />
            <Tooltip formatter={(v) => `${v} %`} />
            <Legend />
            <Bar dataKey="gross_yield_pct" name="Rendement brut" fill={COLOR_PRIMARY} radius={[6, 6, 0, 0]} />
            <Bar dataKey="net_yield_pct" name="Rendement net" fill={COLOR_ACCENT} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* --------------------- Cashflow --------------------- */
function CashflowTab() {
  const [dash, setDash] = useState(null);
  useEffect(() => {
    api.get("/performance/dashboard").then(({ data }) => setDash(data));
  }, []);
  if (!dash) return <Loading />;
  const t = dash.totals;
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <KpiCard name="cf-monthly-group" label="Cash-flow groupe /mois" icon={Coins} tone="accent" value={formatEuro(t.cashflow_monthly)} />
        <KpiCard name="cf-annual-group" label="Cash-flow groupe /an" icon={Wallet} value={formatEuro(t.cashflow_annual)} />
        <KpiCard name="rent-actual" label="Loyers réels /an" icon={Banknote} value={formatEuro(t.annual_rent_actual)} />
        <KpiCard name="credit-payment" label="Mensualités crédit" icon={Building2} value={formatEuro(t.monthly_credit_payment)} />
      </section>
      <div className="flex justify-end">
        <CashflowDetailButton />
      </div>
      <div className="card-elevated overflow-x-auto">
        <div className="px-6 py-4 border-b bg-secondary/40">
          <h3 className="font-heading text-lg">Cash-flow consolidé par SCI</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/20 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Société</th>
              <th className="px-6 py-3 text-right">Immeubles</th>
              <th className="px-6 py-3 text-right">Cash-flow /mois</th>
              <th className="px-6 py-3 text-right">Cash-flow /an</th>
              <th className="px-6 py-3 text-right">Dette</th>
              <th className="px-6 py-3 text-right">Valeur</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(dash.per_company || []).map((c) => (
              <tr key={c.company_id} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{c.company_name}</td>
                <td className="px-6 py-3 text-right font-mono">{c.building_count}</td>
                <td className={`px-6 py-3 text-right font-mono ${c.cashflow_monthly >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEuro(c.cashflow_monthly)}</td>
                <td className={`px-6 py-3 text-right font-mono ${c.cashflow_annual >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEuro(c.cashflow_annual)}</td>
                <td className="px-6 py-3 text-right font-mono text-muted-foreground">{formatEuro(c.remaining_debt)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(c.current_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- Projection --------------------- */
function ProjectionTab() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/performance/projection?months=${months}`).then(({ data }) => setData(data));
  }, [months]);
  if (!data) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Label>Horizon</Label>
        <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="projection-months"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[6, 12, 24, 36, 60].map((m) => <SelectItem key={m} value={String(m)}>{m} mois</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          Moyenne mensuelle : <strong className="text-foreground">{formatEuro(data.average_monthly)}</strong> —
          Cumul : <strong className="text-foreground">{formatEuro(data.total_cumulative)}</strong>
        </div>
      </div>
      <div className="card-elevated p-6">
        <h3 className="font-heading text-lg mb-4">Trésorerie prévisionnelle</h3>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={data.projection}>
            <defs>
              <linearGradient id="gradCum" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COLOR_EMERALD} stopOpacity={0.35} />
                <stop offset="100%" stopColor={COLOR_EMERALD} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" />
            <XAxis dataKey="month" stroke="#8B8578" />
            <YAxis stroke="#8B8578" tickFormatter={(v) => `${Math.round(v / 1000)}k€`} />
            <Tooltip formatter={(v) => formatEuro(v)} />
            <Legend />
            <Area type="monotone" dataKey="cumulative" name="Cumul" stroke={COLOR_EMERALD} fill="url(#gradCum)" />
            <Line type="monotone" dataKey="net_cashflow" name="Cash-flow net" stroke={COLOR_PRIMARY} strokeWidth={2} dot={{ r: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 sticky top-0">
            <tr className="text-left uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Mois</th>
              <th className="px-4 py-3 text-right">Loyers</th>
              <th className="px-4 py-3 text-right">Charges</th>
              <th className="px-4 py-3 text-right">Crédits</th>
              <th className="px-4 py-3 text-right">Impôts</th>
              <th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3 text-right">Cumul</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border font-mono">
            {data.projection.map((r) => (
              <tr key={r.n}>
                <td className="px-4 py-2">{r.month}</td>
                <td className="px-4 py-2 text-right">{formatEuro(r.rents_in)}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{formatEuro(-r.charges_out)}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{formatEuro(-r.credits_out)}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{formatEuro(-r.taxes_out)}</td>
                <td className={`px-4 py-2 text-right font-semibold ${r.net_cashflow >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEuro(r.net_cashflow)}</td>
                <td className={`px-4 py-2 text-right ${r.cumulative >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEuro(r.cumulative)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- Targets --------------------- */
function TargetsTab() {
  const [comparison, setComparison] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ scope: "building", ref_id: "", monthly_target: "", tolerance_pct: "10", label: "" });

  const load = async () => {
    const { data } = await api.get("/performance/targets/comparison");
    setComparison(data.items || []);
  };
  useEffect(() => {
    load();
    api.get("/buildings").then(({ data }) => setBuildings(data.items || []));
    api.get("/companies").then(({ data }) => setCompanies((data.items || []).filter((c) => c.management_mode !== "reference")));
  }, []);

  const submit = async () => {
    try {
      await api.post("/performance/targets", {
        scope: form.scope, ref_id: form.ref_id,
        monthly_target: form.monthly_target, tolerance_pct: form.tolerance_pct,
        label: form.label || undefined,
      });
      toast.success("Objectif ajouté");
      setOpen(false);
      setForm({ scope: "building", ref_id: "", monthly_target: "", tolerance_pct: "10", label: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'ajouter");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet objectif ?")) return;
    await api.delete(`/performance/targets/${id}`);
    toast.success("Objectif supprimé");
    load();
  };

  const options = form.scope === "building"
    ? buildings.map((b) => ({ id: b.id, label: b.name }))
    : form.scope === "company"
      ? companies.map((c) => ({ id: c.id, label: c.denomination || c.name }))
      : [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-heading text-xl">Objectifs de cash-flow</h3>
          <p className="text-sm text-muted-foreground mt-1">Fixez un cash-flow mensuel cible par immeuble ou société, et comparez avec le réel calculé.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="target-add-btn"><PlusCircle className="mr-2 h-4 w-4" /> Nouvel objectif</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Définir un objectif de cash-flow</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type de cible</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, ref_id: "" })}>
                  <SelectTrigger data-testid="target-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="building">Immeuble</SelectItem>
                    <SelectItem value="company">Société</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{form.scope === "building" ? "Immeuble" : "Société"}</Label>
                <Select value={form.ref_id} onValueChange={(v) => setForm({ ...form, ref_id: v })}>
                  <SelectTrigger data-testid="target-ref"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>
                    {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Objectif mensuel (€)</Label><Input type="number" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} data-testid="target-amount" /></div>
                <div><Label>Tolérance (%)</Label><Input type="number" value={form.tolerance_pct} onChange={(e) => setForm({ ...form, tolerance_pct: e.target.value })} /></div>
              </div>
              <div><Label>Libellé (optionnel)</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} data-testid="target-submit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Cible</th>
              <th className="px-6 py-3">Portée</th>
              <th className="px-6 py-3 text-right">Objectif</th>
              <th className="px-6 py-3 text-right">Réel</th>
              <th className="px-6 py-3 text-right">Écart</th>
              <th className="px-6 py-3">Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {comparison.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                <Target className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
                Aucun objectif défini pour l&apos;instant.
              </td></tr>
            )}
            {comparison.map((t) => {
              const tone = t.status === "on_track" ? "success" : t.status === "above" ? "success" : "danger";
              const statusLabel = t.status === "on_track" ? "Sur objectif" : t.status === "above" ? "Dépassé" : "Sous objectif";
              return (
                <tr key={t.id} data-testid={`target-row-${t.id}`} className="hover:bg-secondary/30">
                  <td className="px-6 py-3 font-medium">{t.label || "—"}</td>
                  <td className="px-6 py-3 text-xs text-muted-foreground capitalize">{t.scope}</td>
                  <td className="px-6 py-3 text-right font-mono">{formatEuro(t.monthly_target)}</td>
                  <td className="px-6 py-3 text-right font-mono">{formatEuro(t.actual_monthly)}</td>
                  <td className={`px-6 py-3 text-right font-mono ${t.delta_monthly >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {t.delta_monthly >= 0 ? <ArrowUpRight className="inline h-3 w-3" /> : <ArrowDownRight className="inline h-3 w-3" />}
                    {formatEuro(t.delta_monthly)} ({formatPercent(t.delta_pct)})
                  </td>
                  <td className="px-6 py-3"><StatusBadge tone={tone}>{statusLabel}</StatusBadge></td>
                  <td className="px-6 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => remove(t.id)}>Supprimer</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- Simulations --------------------- */
function SimulationsTab() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ResaleSim />
      <RefinanceSim />
    </div>
  );
}

function ResaleSim() {
  const [buildings, setBuildings] = useState([]);
  const [form, setForm] = useState({ building_id: "", selling_price: "", selling_fees_pct: "6", early_repayment_penalty_pct: "3", years_of_ownership: "" });
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/buildings").then(({ data }) => setBuildings(data.items || [])); }, []);
  const run = async () => {
    try {
      const payload = {
        building_id: form.building_id,
        selling_price: form.selling_price,
        selling_fees_pct: form.selling_fees_pct,
        early_repayment_penalty_pct: form.early_repayment_penalty_pct,
      };
      if (form.years_of_ownership) payload.years_of_ownership = parseInt(form.years_of_ownership);
      const { data } = await api.post("/performance/simulate/resale", payload);
      setResult(data);
      toast.success("Simulation calculée");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation impossible"); }
  };
  return (
    <div className="card-elevated p-6">
      <h3 className="font-heading text-lg mb-4">Simulation de revente</h3>
      <div className="space-y-3">
        <div>
          <Label>Immeuble</Label>
          <Select value={form.building_id} onValueChange={(v) => setForm({ ...form, building_id: v })}>
            <SelectTrigger data-testid="resale-building"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
            <SelectContent>
              {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prix de vente (€)</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="resale-price" /></div>
          <div><Label>Frais de vente (%)</Label><Input type="number" step="0.1" value={form.selling_fees_pct} onChange={(e) => setForm({ ...form, selling_fees_pct: e.target.value })} /></div>
          <div><Label>Pénalité rembours. anticipé (%)</Label><Input type="number" step="0.1" value={form.early_repayment_penalty_pct} onChange={(e) => setForm({ ...form, early_repayment_penalty_pct: e.target.value })} /></div>
          <div><Label>Années de détention (IR)</Label><Input type="number" value={form.years_of_ownership} onChange={(e) => setForm({ ...form, years_of_ownership: e.target.value })} data-testid="resale-years" /></div>
        </div>
        <Button onClick={run} disabled={!form.building_id || !form.selling_price} className="btn-pill w-full" data-testid="resale-run"><PlayCircle className="mr-2 h-4 w-4" /> Simuler la revente</Button>
      </div>
      {result && (
        <div className="mt-6 space-y-2 text-sm">
          <ResultRow label="Prix de vente" value={formatEuro(result.inputs.selling_price)} />
          <ResultRow label="− Frais" value={formatEuro(-result.fees)} />
          <ResultRow label="− Remboursement anticipé (CRD)" value={formatEuro(-result.remaining_debt)} />
          <ResultRow label="− Pénalité" value={formatEuro(-result.penalty)} />
          <ResultRow label="Produit brut" value={formatEuro(result.net_produit_before_tax)} strong />
          <ResultRow label="− Impôt plus-value" value={formatEuro(-result.capital_gain_tax)} />
          <div className="rounded-xl bg-emerald-50 p-4 mt-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Produit net après impôt</div>
            <div className="mt-1 font-serif text-2xl text-emerald-900">{formatEuro(result.net_produit_after_tax)}</div>
            <div className="text-xs text-emerald-800 mt-1">ROI : {formatPercent(result.roi_pct)} · Plus-value brute : {formatEuro(result.gross_capital_gain)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function RefinanceSim() {
  const [buildings, setBuildings] = useState([]);
  const [form, setForm] = useState({ building_id: "", new_nominal_rate: "", new_duration_months: "", new_insurance_rate: "", refinance_fees_pct: "1" });
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/buildings").then(({ data }) => setBuildings(data.items || [])); }, []);
  const run = async () => {
    try {
      const payload = {
        building_id: form.building_id,
        new_nominal_rate: form.new_nominal_rate,
        new_duration_months: parseInt(form.new_duration_months),
        refinance_fees_pct: form.refinance_fees_pct,
      };
      if (form.new_insurance_rate) payload.new_insurance_rate = form.new_insurance_rate;
      const { data } = await api.post("/performance/simulate/refinance", payload);
      setResult(data);
      toast.success("Simulation calculée");
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation impossible"); }
  };
  return (
    <div className="card-elevated p-6">
      <h3 className="font-heading text-lg mb-4">Simulation de refinancement</h3>
      <p className="text-xs text-muted-foreground mb-4">Rachat groupé de tous les crédits actifs de l&apos;immeuble sélectionné.</p>
      <div className="space-y-3">
        <div>
          <Label>Immeuble</Label>
          <Select value={form.building_id} onValueChange={(v) => setForm({ ...form, building_id: v })}>
            <SelectTrigger data-testid="refi-building"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
            <SelectContent>
              {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nouveau taux (%)</Label><Input type="number" step="0.01" value={form.new_nominal_rate} onChange={(e) => setForm({ ...form, new_nominal_rate: e.target.value })} data-testid="refi-rate" /></div>
          <div><Label>Nouvelle durée (mois)</Label><Input type="number" value={form.new_duration_months} onChange={(e) => setForm({ ...form, new_duration_months: e.target.value })} data-testid="refi-duration" /></div>
          <div><Label>Nouveau taux assurance (%)</Label><Input type="number" step="0.01" value={form.new_insurance_rate} onChange={(e) => setForm({ ...form, new_insurance_rate: e.target.value })} /></div>
          <div><Label>Frais dossier (%)</Label><Input type="number" step="0.1" value={form.refinance_fees_pct} onChange={(e) => setForm({ ...form, refinance_fees_pct: e.target.value })} /></div>
        </div>
        <Button onClick={run} disabled={!form.building_id || !form.new_nominal_rate || !form.new_duration_months} className="btn-pill w-full" data-testid="refi-run"><PlayCircle className="mr-2 h-4 w-4" /> Simuler le refinancement</Button>
      </div>
      {result && (
        <div className="mt-6 grid grid-cols-2 gap-3">
          <SimBox label="Mensualité actuelle" value={formatEuro(result.current.monthly_payment)} />
          <SimBox label="Mensualité simulée" value={formatEuro(result.simulated.monthly_payment_total)} tone="accent" />
          <SimBox label="Impact cash-flow /mois" value={formatEuro(result.monthly_cashflow_impact)} tone={result.monthly_cashflow_impact >= 0 ? "success" : "warning"} />
          <SimBox label="Économie nette (après frais)" value={formatEuro(result.savings_net_after_fees)} tone={result.savings_net_after_fees >= 0 ? "success" : "warning"} />
        </div>
      )}
    </div>
  );
}

/* --------------------- History --------------------- */
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/performance/history");
    setItems(data.items || []);
    const { data: ev } = await api.get("/performance/evolution");
    setEvolution(ev.series || []);
  };
  useEffect(() => { load(); }, []);

  const capture = async () => {
    setLoading(true);
    try {
      await api.post("/performance/snapshot");
      toast.success("Snapshot capturé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl">Historique annuel</h3>
          <p className="text-sm text-muted-foreground mt-1">Capturez chaque année un instantané des KPIs pour tracer l&apos;évolution du patrimoine.</p>
        </div>
        <Button onClick={capture} disabled={loading} className="btn-pill" data-testid="snapshot-btn">
          <History className="mr-2 h-4 w-4" /> {loading ? "Capture…" : "Capturer maintenant"}
        </Button>
      </div>

      <div className="card-elevated p-6">
        <h3 className="font-heading text-lg mb-4">Évolution des rendements</h3>
        {evolution.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={evolution}>
              <CartesianGrid stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="year" stroke="#8B8578" />
              <YAxis stroke="#8B8578" tickFormatter={(v) => `${v} %`} />
              <Tooltip formatter={(v) => `${v} %`} />
              <Legend />
              <Line type="monotone" dataKey="gross_yield_pct" name="Rdmt brut" stroke={COLOR_PRIMARY} strokeWidth={2.5} />
              <Line type="monotone" dataKey="net_yield_pct" name="Rdmt net" stroke={COLOR_ACCENT} strokeWidth={2.5} />
            </LineChart>
          </ResponsiveContainer>
        ) : <EmptyState label="Capturez un premier snapshot pour tracer l'évolution." />}
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="text-left">
              <th className="px-6 py-3">Année</th>
              <th className="px-6 py-3 text-right">Patrimoine net</th>
              <th className="px-6 py-3 text-right">Dette</th>
              <th className="px-6 py-3 text-right">Loyers /an</th>
              <th className="px-6 py-3 text-right">Cash-flow /an</th>
              <th className="px-6 py-3 text-right">Rdmt net</th>
              <th className="px-6 py-3">Capturé le</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">Aucun instantané enregistré.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} data-testid={`snapshot-row-${s.year}`} className="hover:bg-secondary/30">
                <td className="px-6 py-3 font-medium">{s.year}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(s.totals.patrimoine_net)}</td>
                <td className="px-6 py-3 text-right font-mono text-muted-foreground">{formatEuro(s.totals.remaining_debt)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEuro(s.totals.annual_rent_actual)}</td>
                <td className={`px-6 py-3 text-right font-mono ${s.totals.cashflow_annual >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatEuro(s.totals.cashflow_annual)}</td>
                <td className="px-6 py-3 text-right font-mono">{formatPercent(s.yields_pct.net)}</td>
                <td className="px-6 py-3 text-xs text-muted-foreground">{new Date(s.captured_at).toLocaleDateString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------- Bits & helpers --------------------- */
function Loading({ label = "Chargement…", onRetry } = {}) {
  return <SmartLoader label={label} onRetry={onRetry} compact />;
}
function EmptyState({ label }) { return <div className="text-center text-sm text-muted-foreground py-12">{label}</div>; }
function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-0.5 font-mono">{value}</div></div>;
}

function CoherenceAlerts({ alerts }) {
  if (!alerts?.length) return null;
  return (
    <section data-testid="perf-coherence-alerts" className="space-y-2">
      {alerts.map((a) => {
        const tone = a.severity === "critical"
          ? "border-red-300 bg-red-50 text-red-900"
          : "border-amber-300 bg-amber-50 text-amber-900";
        const Icon = AlertTriangle;
        return (
          <div key={a.code} className={`rounded-xl border p-4 flex items-start gap-3 ${tone}`}>
            <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading text-sm">{a.title}</div>
              <p className="mt-1 text-xs leading-relaxed">{a.detail}</p>
              {a.action_href && (
                <a href={a.action_href} className="mt-2 inline-flex items-center gap-1 text-xs font-medium underline">
                  {a.action_label || "Voir le détail"} <ChevronRight className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function ChargesInfo({ total }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // charge_id being reclassified

  const loadBreakdown = async () => {
    try {
      const { data: d } = await api.get("/performance/charges-breakdown");
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Détail indisponible");
    }
  };

  useEffect(() => { if (open) loadBreakdown(); }, [open]);

  const reclassify = async (id) => {
    if (!window.confirm("Archiver cette charge ? Elle n'entrera plus dans le calcul des charges annuelles. Cette action est traçable.")) return;
    setBusy(id);
    try {
      await api.post(`/performance/charges/${id}/reclassify`);
      toast.success("Charge archivée — vous pouvez maintenant créer un crédit si besoin");
      await loadBreakdown();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reclassement impossible");
    } finally { setBusy(null); }
  };

  return (
    <>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          Charges annuelles
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-accent hover:underline"
            data-testid="charges-breakdown-btn"
            title="Voir le détail des charges"
          >
            <InfoIcon className="h-3 w-3 inline" />
          </button>
        </div>
        <div className="mt-0.5 font-mono">{formatEuro(total)}</div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="charges-breakdown-dialog">
          <DialogHeader>
            <DialogTitle>Détail des charges annuelles</DialogTitle>
          </DialogHeader>
          {!data && <Loading />}
          {data && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">{data.hint}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-secondary/40 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
                  <div className="mt-1 font-serif text-2xl">{formatEuro(data.total_annual_charges)}</div>
                </div>
                <div className={`rounded-xl p-3 ${data.suspicious_total > 0 ? "bg-amber-50 border border-amber-200" : "bg-secondary/40"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dont suspect</div>
                  <div className="mt-1 font-serif text-2xl">{formatEuro(data.suspicious_total)}</div>
                </div>
              </div>

              {data.items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">Aucune charge enregistrée.</div>}

              <ul className="divide-y divide-border">
                {data.items.map((it, i) => (
                  <li key={it.id || `common-${i}`} data-testid={`charge-item-${it.id || i}`} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{it.label || "(sans libellé)"}</span>
                          {it.suspicious && (
                            <StatusBadge tone="warning" className="text-[10px]">Suspect</StatusBadge>
                          )}
                          {it.detected_doc_type && (
                            <code className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/50">{it.detected_doc_type}</code>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {it.building_name || "—"} · {it.source} {it.supplier ? `· ${it.supplier}` : ""}
                        </div>
                        {it.reason && (
                          <div className="mt-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            ⚠ {it.reason}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-semibold">{formatEuro(it.amount)}</div>
                        {it.id && it.suspicious && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => reclassify(it.id)}
                            disabled={busy === it.id}
                            data-testid={`charge-reclassify-${it.id}`}
                          >
                            {busy === it.id ? "…" : "Archiver"}
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <DialogFooter>
                <Button variant="ghost" onClick={loadBreakdown}>
                  <RefreshCw className="h-3 w-3 mr-1" /> Rafraîchir
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CashflowDetailButton({ buildingId = null }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setErr(null);
      try {
        const { data: d } = await api.get("/performance/cashflow-detail",
          { params: buildingId ? { building_id: buildingId } : {} });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Détail indisponible");
      }
    })();
    return () => { cancelled = true; };
  }, [open, buildingId]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="cashflow-detail-btn"
      >
        <InfoIcon className="h-3.5 w-3.5 mr-1.5" /> Voir le détail du calcul
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"
                       data-testid="cashflow-detail-dialog">
          <DialogHeader>
            <DialogTitle>Détail du calcul du cash-flow</DialogTitle>
          </DialogHeader>
          {err && <div className="text-sm text-red-600">{err}</div>}
          {!data && !err && <Loading />}
          {data && (
            <div className="space-y-4 text-sm">
              <div className="text-xs text-muted-foreground italic">{data.formula}</div>

              {data.warnings?.length > 0 && (
                <div className="space-y-2">
                  {data.warnings.map((w) => (
                    <div key={w.code} className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`cf-warning-${w.code}`}>
                      <div className="flex items-center gap-2 font-medium text-amber-900">
                        <AlertTriangle className="h-4 w-4" /> {w.title}
                      </div>
                      <p className="mt-1 text-xs text-amber-900">{w.detail}</p>
                      {w.items && w.items.length > 0 && (
                        <ul className="mt-2 text-xs space-y-0.5">
                          {w.items.map((it) => (
                            <li key={it.lot_id} className="flex justify-between">
                              <span className="font-mono">{it.reference || it.lot_id}</span>
                              <span className="font-mono">{formatEuro(it.target_monthly)} /mois</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Section title="Revenus locatifs">
                <Row label="Loyers cibles annuels" value={formatEuro(data.revenue.loyers_cibles_annuels)} muted />
                <Row label="Loyers perçus annuels" value={formatEuro(data.revenue.loyers_percus_annuels)} strong positive />
              </Section>

              <Section title="Charges" total={data.charges.total_annuel}>
                {data.charges.buckets.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-2">Aucune charge enregistrée.</div>
                ) : (
                  data.charges.buckets.map((b) => (
                    <Row key={b.label} label={b.label} value={formatEuro(b.amount)} negative />
                  ))
                )}
                <Row label="Dont récupérables" value={formatEuro(data.charges.recuperables)} muted small />
                <Row label="Dont non récupérables" value={formatEuro(data.charges.non_recuperables)} muted small />
              </Section>

              <Section title="Crédits" total={data.credit.mensualite_annuelle}>
                <Row label={`Mensualités annuelles (${data.credit.count} crédit${data.credit.count > 1 ? "s" : ""})`}
                     value={formatEuro(data.credit.mensualite_annuelle)} negative />
                <Row label="Dont capital remboursé" value={formatEuro(data.credit.dont_capital)} muted small />
                <Row label="Dont intérêts 12 mois" value={formatEuro(data.credit.dont_interets_12m)} muted small />
                <Row label="Dont assurance emprunteur" value={formatEuro(data.credit.dont_assurance)} muted small />
              </Section>

              <Section title="Fiscalité">
                <Row label={`Impôts estimés (${data.tax.taux_effectif_pct}%)`}
                     value={formatEuro(data.tax.impots_estimes)} negative />
                <Row label="Assiette imposable" value={formatEuro(data.tax.assiette)} muted small />
              </Section>

              <div className={`rounded-xl p-4 border-2 ${data.cashflow.annuel >= 0 ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
                   data-testid="cf-result">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Cash-flow final</div>
                <div className="mt-2 flex items-baseline justify-between">
                  <div>
                    <div className={`font-serif text-3xl ${data.cashflow.annuel >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      {formatEuro(data.cashflow.annuel)} <span className="text-sm text-muted-foreground">/ an</span>
                    </div>
                    <div className={`mt-1 text-sm ${data.cashflow.mensuel >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      Soit {formatEuro(data.cashflow.mensuel)} par mois
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, total, children }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{title}</div>
        {total !== undefined && total !== null && (
          <div className="text-xs font-mono font-semibold">{formatEuro(total)}</div>
        )}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, strong = false, muted = false, small = false, positive = false, negative = false }) {
  const tone = positive ? "text-emerald-700" : negative ? "text-red-700" : "";
  return (
    <div className={`flex justify-between ${small ? "text-xs" : "text-sm"} ${muted ? "text-muted-foreground" : ""}`}>
      <span className={strong ? "font-medium" : ""}>{label}</span>
      <span className={`font-mono ${strong ? "font-semibold" : ""} ${tone}`}>{value}</span>
    </div>
  );
}

function ResultRow({ label, value, strong }) {
  return (
    <div className={`flex justify-between border-b border-border py-1.5 ${strong ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
function SimBox({ label, value, tone }) {
  const bg = tone === "success" ? "bg-emerald-50 text-emerald-800"
    : tone === "warning" ? "bg-amber-50 text-amber-800"
    : tone === "accent" ? "bg-accent/10 text-primary"
    : "bg-secondary text-foreground";
  return (
    <div className={`rounded-xl p-4 ${bg}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest">{label}</div>
      <div className="mt-1 font-serif text-xl">{value}</div>
    </div>
  );
}
function YieldBar({ label, value, color }) {
  const pct = useMemo(() => Math.max(0, Math.min(15, Number(value) || 0)) / 15 * 100, [value]);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-mono font-semibold">{formatPercent(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
